// Model blogu (issue #34) — JEDINÉ místo, kde se čte nativní DOM rubrik a článků.
// Konzumenti: articlesPage.js (výpis rubriky) a articleDetail.js (detail článku).
//
// Nativní strom výpisu (`body.type-posts-listing`, /rubriky/<slug>/):
//   main#content > .content-inner > .sectionDescription > h1
//                                 > ul.subcategories.with-image > li > a > .image + .text
//                                 > #newsWrapper > article.news-item
//                                     > a.news-item-link-img > img.news-image
//                                     > h2 > a[data-testid=textArticleTitle]
//                                     > .description > p
//                                 > .listingControls > nav.pagination + .itemsTotal + .goToTop
//
// ODCHYLKA OD SPECKY: `screens/20-blog.md` tvrdí, že navigace mezi rubrikami
// nativně neexistuje a musí být náš statický blok. NEPLATÍ — Shoptet na výpisu
// renderuje `ul.subcategories` se VŠEMI sourozeneckými rubrikami (aktuální mezi
// nimi není, ta je v H1). Lištu tedy stavíme z reálných dat, ne natvrdo.
//
// CO VE VÝPISU NENÍ: datum vydání ani doba čtení položek (žijí jen na detailu
// ve schema meta) a počty článků cizích rubrik (Shoptet vypisuje jen celkový
// počet AKTUÁLNÍ rubriky). Nevymýšlíme je — dotahují se ze stránek, kde reálně
// jsou (`hydrate*` níže): detail článku dá datum i délku textu, výpis cizí
// rubriky dá její `.itemsTotal`. Odpovědi jdou do `localStorage` s TTL, takže
// druhá návštěva blogu už nefetchuje (datum článku se nemění, počty rubrik
// pomalu) a fetche jedou s malou souběžností až po `requestIdleCallback`.

import { page } from './pages.js';
import L from '../locale/cs/blogModel.js';

// Ikony rubrik dle mockupu (design/Blog výpis.dc.html) — stejná kurace jako
// CAT_ICONS v menuModel.js; slug → symbol našeho spritu.
export const RUBRIC_ICONS = {
  'rady-a-tipy': 'info',
  aktuality: 'bolt',
  'auto-moto-2': 'car',
  grilovani: 'gift',
  'zahrada-2': 'leaf',
  inspirace: 'star',
  'dilna-2': 'wrench',
  'dum-a-stavba': 'hammer',
};
const ICON_FALLBACK = 'file';

export function textOf(node) {
  return node ? (node.textContent || '').replace(/\s+/g, ' ').trim() : '';
}

export function rubricSlug(href) {
  const base = page('blog');
  const i = base ? String(href || '').indexOf(base) : -1;
  if (i < 0) return '';
  const m = /^[^/?#]+/.exec(String(href).slice(i + base.length));
  return m ? m[0] : '';
}

export function rubricIcon(href) {
  return RUBRIC_ICONS[rubricSlug(href)] || ICON_FALLBACK;
}

// Skloňování počtu článků (1 článek / 2–4 články / 5+ článků)
export function articlesWord(n) {
  return L.articlesWord(n);
}

// --------------------------------------------------------------------------
// Výpis rubriky
// --------------------------------------------------------------------------

// Cesta aktuální rubriky — z posledního stupně breadcrumbu (`meta[itemprop=item]`
// nese kanonickou URL rubriky, tedy i z „strana-3“ míří na její začátek).
export function currentPath() {
  const meta = document.querySelector('.breadcrumbs [data-testid="breadcrumbsLastLevel"] meta[itemprop="item"]');
  const value = meta ? meta.getAttribute('content') : null;
  if (value) {
    try {
      return new URL(value, window.location.origin).pathname;
    } catch (e) {
      /* nevalidní URL → padáme na location */
    }
  }
  return window.location.pathname;
}

// Aktuální rubrika + všichni sourozenci. Aktivní jde první (nativní seznam
// pořadí aktuální rubriky neprozradí, tak ji nemá kam vsunout).
export function readRubrics() {
  const out = [];
  const h1 = document.querySelector('#content .sectionDescription h1, #content h1');
  if (h1) {
    out.push({ href: currentPath(), name: textOf(h1), active: true, li: null, link: null });
  }
  const list = document.querySelector('#content ul.subcategories');
  if (list) {
    const items = list.querySelectorAll(':scope > li');
    for (let i = 0; i < items.length; i += 1) {
      const li = items[i];
      const link = li.querySelector('a');
      if (!link) continue;
      out.push({
        href: link.getAttribute('href'),
        name: textOf(li.querySelector('.text')) || textOf(link),
        active: false,
        li: li,
        link: link,
      });
    }
  }
  return out;
}

// Celkový počet článků rubriky — nativní `.itemsTotal > strong` (42 položek celkem).
// `root` umožní totéž nad dokumentem z fetch + DOMParser (počty cizích rubrik).
export function totalArticles(root) {
  const node = (root || document).querySelector('.listingControls .itemsTotal strong');
  const n = node ? parseInt(textOf(node).replace(/\D/g, ''), 10) : NaN;
  return isFinite(n) ? n : null;
}

// Strana X z Y — nativní `p#paginationStatus` („Nacházíte se na straně 1 z 6.“),
// fallback na `strong.pagination__currentPage`.
export function pageInfo() {
  const status = document.getElementById('paginationStatus');
  const m = /(\d+)\s*z\s*(\d+)/.exec(textOf(status));
  if (m) return { page: parseInt(m[1], 10), pages: parseInt(m[2], 10) };
  const cur = document.querySelector('.pagination__currentPage');
  const current = cur ? parseInt(textOf(cur), 10) : 1;
  return { page: isFinite(current) ? current : 1, pages: 1 };
}

// URL N-té strany. Šablonu bereme z nativního odkazu (`…/strana-6/`), ať se
// nespoléháme na vlastní odhad tvaru cesty; strana 1 je cesta bez segmentu.
export function pageUrlFactory() {
  const sample = document.querySelector('.pagination__link--last, .pagination__link--next, .pagination__link--prev');
  const href = sample ? sample.getAttribute('href') : null;
  const base = href ? href.replace(/strana-\d+\/?$/, '') : window.location.pathname.replace(/strana-\d+\/?$/, '');
  return function (n) {
    return n <= 1 ? base : base + 'strana-' + n + '/';
  };
}

// Položky výpisu — funguje i nad dokumentem z fetch + DOMParser.
export function parseArticles(root) {
  const items = (root || document).querySelectorAll('#newsWrapper .news-item, .news-wrapper .news-item');
  const out = [];
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    const title = item.querySelector('h2 a');
    const img = item.querySelector('img.news-image');
    if (!title) continue;
    out.push({
      href: title.getAttribute('href'),
      title: textOf(title),
      img: img ? img.getAttribute('src') || img.getAttribute('data-src') : null,
      alt: img ? img.getAttribute('alt') || '' : '',
      perex: textOf(item.querySelector('.description p')),
    });
  }
  return out;
}

// --------------------------------------------------------------------------
// Detail článku
// --------------------------------------------------------------------------

// Rubrika článku = první `/rubriky/` odkaz v nativním breadcrumbu. Body třída
// `in-<slug>` ukazuje jen POSLEDNÍ rubriku (článek jich může mít víc), breadcrumb
// je spolehlivější a nese i název.
export function currentRubric() {
  if (!page('blog')) return null;
  const links = document.querySelectorAll('.breadcrumbs a[href*="' + page('blog') + '"]');
  for (let i = 0; i < links.length; i += 1) {
    const a = links[i];
    const name = textOf(a.querySelector('[itemprop="name"]') || a);
    if (name) return { href: a.getAttribute('href'), name: name };
  }
  return null;
}

// „2024-10-10T14:25:40+0200“ → „10. 10. 2024“
export function formatDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.getDate() + '. ' + (d.getMonth() + 1) + '. ' + d.getFullYear();
}

// Odhad doby čtení z DÉLKY CELÉHO TEXTU (200 slov/min). Na výpisu se nepočítá —
// tam je k dispozici jen zkrácený perex a odhad z něj by lhal.
export function readMinutes(text) {
  const words = (text || '').trim().split(/\s+/).filter(Boolean).length;
  if (!words) return null;
  return Math.max(1, Math.round(words / 200));
}

export function readLabel(text) {
  const min = readMinutes(text);
  return min ? min + L.readMinutesSuffix : null;
}

// Krátký tvar pro karty mřížky („4 min“), dlouhý zůstává heru a detailu.
export function readShort(text) {
  const min = readMinutes(text);
  return min ? min + L.readShortSuffix : null;
}

// --------------------------------------------------------------------------
// Perex výpisu
// --------------------------------------------------------------------------

// Shoptet perex ve výpisu tvrdě usekne na délku ZNAKŮ, takže končí uprostřed
// slova („…stromy vrhající p...“). Doříznutí: nejdřív na konec poslední věty,
// a když by tím zmizelo víc než 40 % textu, aspoň na celé slovo + výpustka.
const SENTENCE_KEEP = 0.6;

export function tidyPerex(text) {
  const raw = (text || '').replace(/\s+/g, ' ').trim();
  if (!raw) return '';
  const cut = raw.replace(/(\s*[.…]{1,3}\s*)$/, '').replace(/\s+\S{1,2}$/, function (tail) {
    // useknutý zbytek slova („ p“) po výpustce; celá krátká slova (a, i, k…) nechat
    return /^\s+(a|i|k|o|s|u|v|z|se|na|do|od|po|za|ve|ke|ze|to|by|je)$/i.test(tail) ? tail : '';
  });
  if (/[.!?]$/.test(cut)) return cut;

  const end = Math.max(cut.lastIndexOf('.'), cut.lastIndexOf('!'), cut.lastIndexOf('?'));
  if (end > 0 && end + 1 >= cut.length * SENTENCE_KEEP) return cut.slice(0, end + 1);

  const space = cut.lastIndexOf(' ');
  return (space > 0 ? cut.slice(0, space) : cut) + '…';
}

// --------------------------------------------------------------------------
// Dohydratování meta dat (datum + doba čtení, počty článků rubrik)
// --------------------------------------------------------------------------

const STORE_KEY = 'bs-blog-meta-v1';
const TTL_META = 7 * 24 * 3600 * 1000; // datum a délka článku se nemění
const TTL_COUNT = 12 * 3600 * 1000; // počet článků rubriky ano, ale pomalu
const CONCURRENCY = 2;

let store = null;

function now() {
  return Date.now();
}

function loadStore() {
  if (store) return store;
  store = {};
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === 'object') store = parsed;
  } catch (e) {
    /* privátní režim / plná kvóta → jedeme jen v paměti */
  }
  // Úklid při načtení: záznam starší než nejdelší TTL už `cacheGet` nikdy
  // nevrátí — bez mazání by se klíče přejmenovaných článků hromadily navždy
  // (code review B5-08). Zapíše se s nejbližším `cacheSet`.
  const maxAge = Math.max(TTL_META, TTL_COUNT);
  Object.keys(store).forEach(function (key) {
    const hit = store[key];
    if (!hit || typeof hit.t !== 'number' || now() - hit.t > maxAge) delete store[key];
  });
  return store;
}

function saveStore() {
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(store));
  } catch (e) {
    /* zápis nesmí shodit stránku */
  }
}

export function cacheGet(key, ttl) {
  const hit = loadStore()[key];
  if (!hit || typeof hit.t !== 'number' || now() - hit.t > ttl) return null;
  return hit.v;
}

export function cacheSet(key, value) {
  loadStore()[key] = { t: now(), v: value };
  saveStore();
}

// jen pro testy/probe — vyprázdní paměťovou i uloženou kopii
export function cacheClear() {
  store = {};
  try {
    window.localStorage.removeItem(STORE_KEY);
  } catch (e) {
    /* nevadí */
  }
}

const inflight = {};

function fetchDoc(href) {
  if (inflight[href]) return inflight[href];
  const p = fetch(href, { credentials: 'same-origin' })
    .then(function (res) {
      return res.ok ? res.text() : Promise.reject(new Error(String(res.status)));
    })
    .then(function (html) {
      return new DOMParser().parseFromString(html, 'text/html');
    });
  inflight[href] = p;
  return p;
}

// Datum + doba čtení z DETAILU článku (schema meta + délka textu).
export function parseArticleMeta(doc) {
  const meta = doc.querySelector('meta[itemprop="datePublished"]');
  const body = doc.querySelector('.news-item-detail .text, .news-item-detail article, article .text');
  const text = textOf(body);
  return {
    date: formatDate(meta ? meta.getAttribute('content') : null),
    read: readLabel(text),
    readShort: readShort(text),
  };
}

export function fetchArticleMeta(href) {
  const key = 'a:' + href;
  const hit = cacheGet(key, TTL_META);
  if (hit) return Promise.resolve(hit);
  return fetchDoc(href).then(function (doc) {
    const meta = parseArticleMeta(doc);
    if (meta.date || meta.read) cacheSet(key, meta);
    return meta;
  });
}

export function fetchRubricTotal(href) {
  const key = 'r:' + href;
  const hit = cacheGet(key, TTL_COUNT);
  if (hit != null) return Promise.resolve(hit);
  return fetchDoc(href).then(function (doc) {
    const total = totalArticles(doc);
    if (total != null) cacheSet(key, total);
    return total;
  });
}

// Fronta úkolů `{href, load(href) → Promise, apply(value)}` s malou souběžností.
// Chyba jedné položky nesmí zastavit ostatní ani nic vypsat do konzole —
// bez dat prostě zůstane karta bez meta řádku.
export function hydrate(tasks) {
  if (!window.fetch || !window.DOMParser || !tasks.length) return Promise.resolve();
  const queue = tasks.slice();
  const runner = function () {
    const task = queue.shift();
    if (!task) return Promise.resolve();
    return task
      .load(task.href)
      .then(function (value) {
        if (value != null) task.apply(value);
      })
      .catch(function () {
        /* nedostupná stránka → položka zůstane bez meta */
      })
      .then(runner);
  };
  const lanes = [];
  for (let i = 0; i < Math.min(CONCURRENCY, queue.length); i += 1) lanes.push(runner());
  return Promise.all(lanes);
}

// Fetche patří až za první vykreslení — meta je doplněk, ne obsah stránky.
export function whenIdle(fn) {
  if (window.requestIdleCallback) window.requestIdleCallback(fn, { timeout: 1500 });
  else window.setTimeout(fn, 300);
}
